import Dexie, { type EntityTable } from 'dexie';
import type {
  AchievementRow,
  Attempt,
  CalibrationRow,
  ChestRow,
  CosmeticRow,
  QuestRow,
  KvRow,
  LessonProgress,
  Profile,
  SessionRecord,
  SkillState,
  SongScoreRow,
  SyncItem,
} from './types';

/**
 * Local-first store. Every user write lands here first and is mirrored to
 * Supabase through the sync queue, so the UI never waits on the network.
 *
 * Schema changes need a new `version()` block with a migration; never edit an
 * existing one, or returning users get a VersionError on open.
 */
class AudigoalDb extends Dexie {
  profile!: EntityTable<Profile, 'id'>;
  skillStates!: EntityTable<SkillState, 'skillId'>;
  lessonProgress!: EntityTable<LessonProgress, 'key'>;
  sessions!: EntityTable<SessionRecord, 'id'>;
  attempts!: EntityTable<Attempt, 'id'>;
  syncQueue!: EntityTable<SyncItem, 'id'>;
  calibration!: EntityTable<CalibrationRow, 'deviceKey'>;
  kv!: EntityTable<KvRow, 'key'>;
  songScores!: EntityTable<SongScoreRow, 'key'>;
  quests!: EntityTable<QuestRow, 'key'>;
  achievements!: EntityTable<AchievementRow, 'achievementId'>;
  chests!: EntityTable<ChestRow, 'id'>;
  cosmetics!: EntityTable<CosmeticRow, 'cosmeticId'>;

  constructor() {
    super('audigoal');
    this.version(1).stores({
      profile: 'id',
      skillStates: 'skillId, dueAt, mastery, dirty',
      lessonProgress: 'key, lessonId, dirty',
      sessions: 'id, startedAt, synced',
      attempts: '++id, sessionId, answeredAt, paramsHash, templateId, correct',
      syncQueue: '++id, kind, createdAt',
      calibration: 'deviceKey',
      kv: 'key',
    });
    // Phase 6: song scores. Adding a table needs no data migration.
    this.version(2).stores({
      songScores: 'key, songId, dirty',
    });
    // Phase 7: quests, achievements, chests and what has been collected.
    this.version(3).stores({
      quests: 'key, periodKey, questId',
      achievements: 'achievementId',
      chests: 'id, openedAt',
      cosmetics: 'cosmeticId',
      inventory: 'itemId',
    });
    // The practice economy: consumables are gone, and with them the inventory.
    this.version(4).stores({
      inventory: null,
    });
  }
}

export const db = new AudigoalDb();

/* --------------------------------------------------------------- key/value */

export async function kvGet<T>(key: string): Promise<T | undefined> {
  const row = await db.kv.get(key);
  return row?.value as T | undefined;
}

export async function kvSet(key: string, value: unknown): Promise<void> {
  await db.kv.put({ key, value });
}

/* ----------------------------------------------------------------- profile */

export async function getLocalProfile(): Promise<Profile | undefined> {
  // Exactly one profile row is kept; `toCollection().first()` avoids needing its id.
  return db.profile.toCollection().first();
}

export async function putLocalProfile(profile: Profile): Promise<void> {
  await db.profile.put(profile);
}

export async function clearLocalProfile(): Promise<void> {
  await db.profile.clear();
}

/* -------------------------------------------------------------- attempts */

/**
 * Recent question fingerprints, used by the generator's history filter so the
 * same question does not come back within the last 200 attempts.
 */
export async function recentParamsHashes(limit = 200): Promise<Set<string>> {
  const rows = await db.attempts.orderBy('answeredAt').reverse().limit(limit).toArray();
  return new Set(rows.map((r) => r.paramsHash));
}

export async function recordAttempts(attempts: Attempt[]): Promise<void> {
  if (attempts.length === 0) return;
  await db.attempts.bulkAdd(attempts);
}

/* ------------------------------------------------------------ skill states */

export async function getSkillStates(): Promise<SkillState[]> {
  return db.skillStates.toArray();
}

export async function putSkillStates(states: SkillState[]): Promise<void> {
  if (states.length === 0) return;
  await db.skillStates.bulkPut(states);
}

/* --------------------------------------------------------- lesson progress */

export function lessonProgressKey(lessonId: string, instrument = ''): string {
  return `${lessonId}|${instrument}`;
}

export async function getLessonProgress(): Promise<LessonProgress[]> {
  return db.lessonProgress.toArray();
}

export async function putLessonProgress(row: LessonProgress): Promise<void> {
  await db.lessonProgress.put(row);
}

/* ---------------------------------------------------------------- sessions */

export async function saveSession(session: SessionRecord, attempts: Attempt[]): Promise<void> {
  await db.transaction('rw', db.sessions, db.attempts, async () => {
    await db.sessions.put(session);
    if (attempts.length > 0) await db.attempts.bulkAdd(attempts);
  });
}

/** Wipes every local table. Used on sign-out when the user asks not to keep data. */
export async function resetLocalData(): Promise<void> {
  await db.transaction(
    'rw',
    [
      db.profile,
      db.skillStates,
      db.lessonProgress,
      db.sessions,
      db.attempts,
      db.syncQueue,
      db.kv,
      db.songScores,
      db.quests,
      db.achievements,
      db.chests,
      db.cosmetics,
    ],
    async () => {
      await Promise.all([
        db.profile.clear(),
        db.skillStates.clear(),
        db.lessonProgress.clear(),
        db.sessions.clear(),
        db.attempts.clear(),
        db.syncQueue.clear(),
        db.kv.clear(),
        db.songScores.clear(),
        db.quests.clear(),
        db.achievements.clear(),
        db.chests.clear(),
        db.cosmetics.clear(),
      ]);
    },
  );
}

/* ------------------------------------------------------------ calibration */

/** Measured microphone latency for one device, or undefined if never measured. */
export async function getCalibration(deviceKey: string): Promise<CalibrationRow | undefined> {
  return db.calibration.get(deviceKey);
}

export async function putCalibration(row: CalibrationRow): Promise<void> {
  await db.calibration.put(row);
}
